import { UseFormReturnType } from '@mantine/form';
import { ApiError } from 'src/error/ApiError';

export type OnboardingSignupFormValues = {
  firstName: string;
  lastName: string;
  companyName: string;
};

export function applySubmissionError(
  error: unknown,
  form: UseFormReturnType<OnboardingSignupFormValues>,
  setTopLevelError: (message: string) => void,
  onAmbiguousTimeout: () => void,
) {
  if (!(error instanceof ApiError)) {
    // A network error or the client-side request timeout means we genuinely don't know
    // whether the backend received and is still processing the submission (this endpoint's
    // synchronous Company/migration/User provisioning can legitimately take several minutes -
    // see DECISIONS.md). While that first submission still holds the invite's PROCESSING
    // claim, resubmitting is rejected outright, so the copy must caution against an
    // immediate retry rather than implying one is safe.
    onAmbiguousTimeout();
    setTopLevelError(
      "We could not confirm whether your submission was received. This can happen if it's taking " +
        'longer than expected to process. Please wait a few minutes and check your email before ' +
        'trying again - resubmitting immediately is unlikely to help and may simply repeat the ' +
        'same delay, since a matching submission may already be in progress. Contact your ' +
        "Afternoon representative if you're not sure.",
    );
    return;
  }

  const message = error.message;

  // Company name and FRN are set on the invite and are not submitted, so an error about
  // either is not something this form can route to a correctable field - it goes top level.
  switch (error.status) {
    case 410:
      setTopLevelError(
        'This link has expired or has already been used to set up an account. Please contact your ' +
          'Afternoon representative to request a new link.',
      );
      return;
    case 404:
      if (message.toLowerCase().includes('frn')) {
        setTopLevelError(
          `${message} Please contact your Afternoon representative to have this corrected.`,
        );
        return;
      }
      setTopLevelError(
        'This link is invalid. Please contact your Afternoon representative to request a new link.',
      );
      return;
    case 409:
      // The duplicate-FRN check rejects an uncorrectable field the same way the 404 FRN
      // case above does, so it gets the same follow-up. The "already being processed"
      // claim conflict is different - retrying shortly may simply work - so it keeps the
      // raw backend message rather than pointing the prospect at a representative.
      if (message.toLowerCase().includes('frn')) {
        setTopLevelError(
          `${message} Please contact your Afternoon representative to have this corrected.`,
        );
        return;
      }
      setTopLevelError(message);
      return;
    default:
      setTopLevelError(message);
  }
}
